import React, { useMemo, useEffect, useRef } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { FiSearch } from 'react-icons/fi';

const libraries = ['places'];

const OptimizeLocalPage = React.memo(({
  searchQuery,
  setSearchQuery,
  searchResults,
  selectedSearchResult,
  setSelectedSearchResult,
  competitorResults,
  showCompetitors,
  isSearchingCompetitors,
  currentBusiness,
  handleSearchSubmit,
  selectSearchResult,
  searchCompetitors,
  setShowCompetitors,
  setCompetitorResults,
  getBusinessEmoji,
  map,
  setMap,
  center,
  placesService,
  setPlacesService,
  mapLoaded,
  setMapLoaded,
  onLoad,
  onUnmount,
  mapContainerStyle,
  mapOptions
}) => {
  const markerClustererRef = useRef(null);
  const currentBusinessMarker = useMemo(() => {
    if (!currentBusiness) return null;
    return {
      position: currentBusiness.position,
      icon: {
        url: `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="18" fill="#ffffff" stroke="#04c0ff" stroke-width="4"/>
            <text x="20" y="28" text-anchor="middle" font-size="18" fill="#000">${currentBusiness.emoji || '🏢'}</text>
          </svg>
        `)}`,
        scaledSize: { width: 40, height: 40 },
        anchor: { x: 20, y: 20 }
      },
      title: currentBusiness.name
    };
  }, [currentBusiness]);

  const competitorMarkers = useMemo(() => {
    if (!showCompetitors || !competitorResults.length) return [];
    return competitorResults.map((competitor) => ({
      key: competitor.id,
      position: competitor.position,
      icon: {
        url: `data:image/svg+xml,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="14" fill="#ffffff" stroke="#ff6b35" stroke-width="3"/>
            <text x="16" y="23" text-anchor="middle" font-size="14" fill="#000">${getBusinessEmoji(competitor)}</text>
          </svg>
        `)}`,
        scaledSize: { width: 32, height: 32 },
        anchor: { x: 16, y: 16 }
      },
      title: `${competitor.name} (Competitor)`
    }));
  }, [showCompetitors, competitorResults, getBusinessEmoji]);

  const selectedBusinessMarker = useMemo(() => {
    if (!selectedSearchResult) return null;
    return {
      position: selectedSearchResult.position,
      icon: {
        url: `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="18" fill="#ffffff" stroke="#04c0ff" stroke-width="4"/>
            <text x="20" y="28" text-anchor="middle" font-size="18" fill="#000">${getBusinessEmoji(selectedSearchResult)}</text>
          </svg>
        `)}`,
        scaledSize: { width: 40, height: 40 },
        anchor: { x: 20, y: 20 }
      },
      title: selectedSearchResult.name,
      zIndex: 1000
    };
  }, [selectedSearchResult, getBusinessEmoji]);

  const limitedSearchResults = useMemo(() => 
    searchResults.slice(0, 3), 
    [searchResults]
  );

  // Setup marker clustering for competitors
  useEffect(() => {
    // Always clean up existing clusterer
    if (markerClustererRef.current) {
      try {
        markerClustererRef.current.clearMarkers();
        markerClustererRef.current.setMap(null);
      } catch (error) {
        console.warn('Error clearing marker clusterer:', error);
      }
      markerClustererRef.current = null;
    }

    // Only create new clusterer if we should show competitors and have enough of them
    if (map && showCompetitors && competitorResults.length > 10 && window.google?.maps?.Marker) {
      try {
        const markers = competitorResults.map((competitor) => {
          const marker = new window.google.maps.Marker({
            position: competitor.position,
            icon: {
              url: `data:image/svg+xml,${encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#ffffff" stroke="#ff6b35" stroke-width="2"/>
                  <text x="12" y="17" text-anchor="middle" font-size="12" fill="#000">${getBusinessEmoji(competitor)}</text>
                </svg>
              `)}`,
              scaledSize: { width: 24, height: 24 },
              anchor: { x: 12, y: 12 }
            },
            title: `${competitor.name} (Competitor)`,
            map: map
          });
          return marker;
        });

        // Use simple clustering approach instead of external library
        if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
          // For newer Google Maps API versions
          markerClustererRef.current = { 
            markers: markers,
            clearMarkers: () => {
              markers.forEach(marker => marker.setMap(null));
            },
            setMap: (map) => {
              markers.forEach(marker => marker.setMap(map));
            }
          };
        } else {
          // Fallback for older versions
          markerClustererRef.current = { 
            markers: markers,
            clearMarkers: () => {
              markers.forEach(marker => marker.setMap(null));
            },
            setMap: (map) => {
              markers.forEach(marker => marker.setMap(map));
            }
          };
        }
      } catch (error) {
        console.error('Error creating marker clusterer:', error);
      }
    }

    return () => {
      if (markerClustererRef.current) {
        try {
          markerClustererRef.current.clearMarkers();
        } catch (error) {
          console.warn('Error in cleanup:', error);
        }
      }
    };
  }, [map, showCompetitors, competitorResults, getBusinessEmoji]);

  return (
    <>
      <div className="page-title">
        <h1>Optimize Locally</h1>
      </div>
      
      <div className="map-section">
        {/* Search Bar */}
        <div className="map-search-bar">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div className="search-input-group">
              <FiSearch className="search-icon" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for businesses..."
                className="search-input"
              />
            </div>
          </form>
        </div>

        {/* Business Cards - Show only when no business is selected */}
        {limitedSearchResults.length > 0 && !selectedSearchResult && (
          <div className="business-cards-container">
            {limitedSearchResults.map(business => (
              <div 
                key={business.id} 
                className={`business-card ${selectedSearchResult?.id === business.id ? 'selected' : ''}`}
                onClick={() => selectSearchResult(business)}
              >
                <div className="business-image">
                  <img src={business.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(business.name)}&background=e0e0e0&color=666`} 
                       alt={business.name} />
                </div>
                <div className="business-info">
                  <h3>{business.name}</h3>
                  <div className="rating">
                    <span className="stars">{'★'.repeat(Math.floor(business.rating))}</span>
                    <span className="rating-text">{business.rating}</span>
                    <span className="review-count">({business.reviewCount})</span>
                  </div>
                  <p className="address">{business.address}</p>
                  {business.isOpen !== null && (
                    <span className={`status ${business.isOpen ? 'open' : 'closed'}`}>
                      {business.isOpen ? 'Open' : 'Closed'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Business Profile Card - Show when a business is selected */}
        {selectedSearchResult && (
          <div className="business-profile-container">
            <div className="business-profile-card">
              <button 
                className="back-button"
                onClick={() => setSelectedSearchResult(null)}
              >
                ← Back to Results
              </button>
              <div className="profile-header">
                <div className="profile-image">
                  <img 
                    src={selectedSearchResult.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedSearchResult.name)}&background=e0e0e0&color=666&size=120`} 
                    alt={selectedSearchResult.name} 
                  />
                </div>
                <div className="profile-info">
                  <h2>{selectedSearchResult.name}</h2>
                  <div className="rating">
                    <span className="stars">{'★'.repeat(Math.floor(selectedSearchResult.rating))}</span>
                    <span className="rating-text">{selectedSearchResult.rating}</span>
                    <span className="review-count">({selectedSearchResult.reviewCount} reviews)</span>
                  </div>
                  <p className="address">{selectedSearchResult.address}</p>
                  {selectedSearchResult.isOpen !== null && (
                    <span className={`status ${selectedSearchResult.isOpen ? 'open' : 'closed'}`}>
                      {selectedSearchResult.isOpen ? 'Open' : 'Closed'}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="analytics-section">
                <div className="analytics-header">
                  <h3>Business Analytics</h3>
                  <button 
                    className="competitors-button"
                    onClick={() => {
                      if (showCompetitors) {
                        setShowCompetitors(false);
                        setCompetitorResults([]);
                      } else {
                        searchCompetitors(selectedSearchResult);
                      }
                    }}
                    disabled={isSearchingCompetitors}
                  >
                    {isSearchingCompetitors ? 'Finding...' : 
                     showCompetitors ? `Hide Competitors (${competitorResults.length})` : 
                     'Show Competitors'}
                  </button>
                </div>
                <div className="analytics-grid">
                  <div className="analytic-item">
                    <span className="analytic-value">{selectedSearchResult.analytics.views.toLocaleString()}</span>
                    <span className="analytic-label">Profile Views</span>
                  </div>
                  <div className="analytic-item">
                    <span className="analytic-value">{selectedSearchResult.analytics.clicks.toLocaleString()}</span>
                    <span className="analytic-label">Clicks</span>
                  </div>
                  <div className="analytic-item">
                    <span className="analytic-value">{selectedSearchResult.analytics.calls.toLocaleString()}</span>
                    <span className="analytic-label">Phone Calls</span>
                  </div>
                  <div className="analytic-item">
                    <span className="analytic-value">{selectedSearchResult.analytics.directions.toLocaleString()}</span>
                    <span className="analytic-label">Directions</span>
                  </div>
                  <div className="analytic-item">
                    <span className="analytic-value">{selectedSearchResult.analytics.websiteClicks.toLocaleString()}</span>
                    <span className="analytic-label">Website Visits</span>
                  </div>
                  <div className="analytic-item">
                    <span className="analytic-value">{selectedSearchResult.analytics.photoViews.toLocaleString()}</span>
                    <span className="analytic-label">Photo Views</span>
                  </div>
                  <div className="analytic-item">
                    <span className="analytic-value">{selectedSearchResult.analytics.conversionRate}%</span>
                    <span className="analytic-label">Conversion Rate</span>
                  </div>
                  <div className="analytic-item">
                    <span className="analytic-value">{selectedSearchResult.analytics.responseRate}%</span>
                    <span className="analytic-label">Response Rate</span>
                  </div>
                </div>
              </div>
              
              {(selectedSearchResult.phone || selectedSearchResult.website) && (
                <div className="contact-section">
                  <h3>Contact Information</h3>
                  <div className="contact-info">
                    {selectedSearchResult.phone && (
                      <a href={`tel:${selectedSearchResult.phone}`} className="contact-link phone">
                        📞 {selectedSearchResult.phone}
                      </a>
                    )}
                    {selectedSearchResult.website && (
                      <a href={selectedSearchResult.website} target="_blank" rel="noopener noreferrer" className="contact-link website">
                        🌐 Visit Website
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Map Container */}
        <div className="map-container">
          {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
            <div className="map-error">
              <h3>🗺️ Map View</h3>
              <p>Google Maps API key required to display interactive map.</p>
              <small>Contact administrator to enable map functionality.</small>
            </div>
          ) : !mapLoaded ? (
            <div className="map-error">
              <p>Failed to load Google Maps. Please check your API key configuration.</p>
            </div>
          ) : (
            <LoadScript 
              googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
              libraries={libraries}
              preventGoogleFontsLoading={true}
              loadingElement={
                <div className="map-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading Map...</p>
                </div>
              }
              onError={(error) => {
                console.error('Google Maps LoadScript Error:', error);
                console.error('API Key:', process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
                setMapLoaded(false);
              }}
              onLoad={() => {
                console.log('Google Maps LoadScript loaded successfully');
                setMapLoaded(true);
              }}
            >
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={12}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={mapOptions}
              >
                {/* Current Business Marker */}
                {currentBusinessMarker && (
                  <Marker
                    position={currentBusinessMarker.position}
                    icon={currentBusinessMarker.icon}
                    title={currentBusinessMarker.title}
                  />
                )}

                {/* Competitor Markers - Only render if showing competitors and less than 10, otherwise use clustering */}
                {showCompetitors && competitorResults.length <= 10 && competitorMarkers.map((marker) => (
                  <Marker
                    key={marker.key}
                    position={marker.position}
                    icon={marker.icon}
                    title={marker.title}
                  />
                ))}

                {/* Selected Business Marker (always on top) */}
                {selectedBusinessMarker && (
                  <Marker
                    position={selectedBusinessMarker.position}
                    icon={selectedBusinessMarker.icon}
                    title={selectedBusinessMarker.title}
                    zIndex={selectedBusinessMarker.zIndex}
                  />
                )}

                {/* Selected Business Info Window */}
                {selectedSearchResult && (
                  <InfoWindow
                    position={selectedSearchResult.position}
                    onCloseClick={() => setSelectedSearchResult(null)}
                  >
                    <div className="info-window">
                      <h4>{selectedSearchResult.name}</h4>
                      <p>{selectedSearchResult.address}</p>
                      <div className="info-rating">
                        <span>{'★'.repeat(Math.floor(selectedSearchResult.rating))}</span>
                        <span> {selectedSearchResult.rating} ({selectedSearchResult.reviewCount} reviews)</span>
                      </div>
                      {selectedSearchResult.phone && (
                        <p>📞 {selectedSearchResult.phone}</p>
                      )}
                      {selectedSearchResult.website && (
                        <p>🌐 <a href={selectedSearchResult.website} target="_blank" rel="noopener noreferrer">Website</a></p>
                      )}
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </LoadScript>
          )}
        </div>
      </div>
    </>
  );
});

OptimizeLocalPage.displayName = 'OptimizeLocalPage';

export default OptimizeLocalPage;